/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react';
import { Box, Button } from '@mui/joy';
import { Print as PrintIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PDFViewer, pdf } from '@react-pdf/renderer';

function printPdfBlob(blob, title) {
  const url = window.URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = String(title || 'Document');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  iframe.src = url;
  document.body.appendChild(iframe);

  const cleanup = () => {
    window.URL.revokeObjectURL(url);
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    const iframeWindow = iframe.contentWindow;
    if (!iframeWindow) {
      cleanup();
      return;
    }
    iframeWindow.focus();
    iframeWindow.print();
    if ('onafterprint' in iframeWindow) {
      iframeWindow.onafterprint = cleanup;
    } else {
      setTimeout(cleanup, 1000);
    }
  };

  iframe.onload = () => setTimeout(triggerPrint, 200);
  setTimeout(triggerPrint, 800);
}

export default function PdfDocumentViewer({
  title,
  children,
  viewerHeight = '350px',
  viewerWidth = '350px',
}) {
  const { t } = useTranslation('ui');
  const [printing, setPrinting] = useState(false);

  const handlePrint = useCallback(async () => {
    if (!children || printing) return;
    setPrinting(true);
    try {
      const blob = await pdf(children).toBlob();
      printPdfBlob(blob, title);
    } catch (e) {
      console.warn('PdfDocumentViewer print error', e);
    } finally {
      setPrinting(false);
    }
  }, [children, printing, title]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<PrintIcon fontSize="small" />}
          onClick={handlePrint}
          loading={printing}
          disabled={!children}
        >
          {t('pdfDocuments.common.printSavePdf')}
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          bgcolor: '#e5e7eb',
          p: 2,
          borderRadius: 1,
          maxHeight: 600,
          overflow: 'auto',
        }}
      >
        <PDFViewer height={viewerHeight} width={viewerWidth}>
          {children}
        </PDFViewer>
      </Box>
    </Box>
  );
}
